"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { isAuthRouteHandlerHref } from "@/lib/document-navigation";
import { type ListingQuery } from "./listing-query";

interface LockedListingActionCardProps {
  pathname: string;
  searchParams: ListingQuery;
  description: string;
  buildPrimaryHref?: (returnTo: string) => string;
  primaryLabel: string;
  onPrimaryClick?: () => void;
  secondaryHref?: string;
  secondaryLabel?: string;
}

export function LockedListingActionCard({
  pathname,
  searchParams,
  description,
  buildPrimaryHref,
  primaryLabel,
  onPrimaryClick,
  secondaryHref,
  secondaryLabel,
}: LockedListingActionCardProps) {
  const links = resolveLockedActionLinks({
    returnTo: buildReturnTo(pathname, searchParams),
    buildPrimaryHref,
    secondaryHref,
    secondaryLabel,
  });

  return (
    <div className="space-y-3">
      <LockedActionButtons
        primaryLabel={primaryLabel}
        onPrimaryClick={onPrimaryClick}
        {...links}
      />
      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

function LockedActionButtons({
  primaryHref,
  primaryLabel,
  onPrimaryClick,
  secondaryHref,
  secondaryLabel,
}: {
  primaryHref?: string;
  primaryLabel: string;
  onPrimaryClick?: () => void;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      {onPrimaryClick ? (
        <Button variant="primary" onClick={onPrimaryClick} className="w-full">
          {primaryLabel}
        </Button>
      ) : primaryHref ? (
        <Button variant="primary" asChild className="w-full">
          {renderActionLink(primaryHref, primaryLabel)}
        </Button>
      ) : null}
      {secondaryHref && secondaryLabel ? (
        <Button asChild variant="outline" className="w-full">
          {renderActionLink(secondaryHref, secondaryLabel)}
        </Button>
      ) : null}
    </div>
  );
}

function renderActionLink(href: string, label: string) {
  return isAuthRouteHandlerHref(href) ? (
    <a href={href}>{label}</a>
  ) : (
    <Link href={href}>{label}</Link>
  );
}

function resolveLockedActionLinks({
  returnTo,
  buildPrimaryHref,
  secondaryHref,
  secondaryLabel,
}: {
  returnTo: string;
  buildPrimaryHref?: (returnTo: string) => string;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  return {
    primaryHref: buildPrimaryHref ? buildPrimaryHref(returnTo) : undefined,
    secondaryHref,
    secondaryLabel,
  };
}

function buildReturnTo(
  pathname: string,
  searchParams: ListingQuery,
) {
  const query = searchParams.toString();
  return query ? `${pathname}?${query}` : pathname;
}
